import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from "react";
import { useApp } from "../store.jsx";
import { Icon } from "./Common.jsx";
import { signOut, authClient } from "../lib/auth-client";
import { useRole } from "../hooks/useRole";
import { useSession } from "../lib/auth-client";
import { getSupportTicketCount } from "../lib/adminApi";
import { getMe, getReviewsUnreadCount } from "../lib/api";

// roles: who can SEE this sidebar item.
// Note: "admin" = platform super admin (cross-tenant). Tenant items must NOT include "admin".
const PAGES = [
  {
    section: "Workspace",
    items: [
      { id: "dashboard", label: "Dashboard", icon: "layout-dashboard", roles: ["owner", "manager", "employee"] },
      { id: "reports", label: "Daily Reports", icon: "file-text", roles: ["owner", "manager", "employee"] },
      { id: "projects", label: "Projects", icon: "folder", roles: ["owner", "manager", "employee"] },
      { id: "projectDetail", label: "Project", icon: "folder", roles: ["owner", "manager", "employee"], hidden: true },
      { id: "managers", label: "Managers", icon: "user-cog", roles: ["owner"] },
      { id: "employees", label: "Employees", icon: "users", roles: ["owner", "manager"] },
      { id: "assignTasks", label: "Assign Tasks", icon: "clipboard-list", roles: ["owner", "manager"] },
      { id: "myTasks", label: "My Tasks", icon: "check-square", roles: ["employee", "manager"] },
      { id: "departments", label: "Departments", icon: "building", roles: ["owner", "manager"] },
      { id: "holidays", label: "Holidays & Leaves", icon: "calendar", roles: ["owner", "manager"] },
      { id: "myCalendar", label: "My Calendar", icon: "calendar", roles: ["employee"] },
      { id: "analytics", label: "Analytics", icon: "bar-chart-2", roles: ["owner", "manager"] },
      { id: "performance", label: "Performance", icon: "award", roles: ["owner", "manager", "employee"], badgeKey: "reviews" },
      { id: "announcements", label: "Announcements", icon: "megaphone", roles: ["owner", "manager", "employee"] },
    ],
  },
  {
    section: "Automation",
    items: [
      { id: "whatsapp", label: "WhatsApp Bot", icon: "message-circle", roles: ["owner", "manager"], live: true },
      { id: "reminders", label: "Reminders", icon: "bell", roles: ["owner", "manager"] },
    ],
  },
  {
    section: "Account",
    items: [
      { id: "billing", label: "Billing", icon: "credit-card", roles: ["owner"] },
      { id: "activityLog", label: "Activity Log", icon: "history", roles: ["owner", "manager"] },
      { id: "latestUpdates", label: "Latest Updates", icon: "sparkles", roles: ["owner", "manager", "employee"] },
      { id: "settings", label: "Settings", icon: "settings", roles: ["owner", "manager", "employee"] },
    ],
  },
  {
    section: "Platform",
    items: [
      { id: "adminOverview",      label: "Overview",      icon: "layout-dashboard", roles: ["admin"] },
      { id: "adminCompanies",     label: "Companies",     icon: "building",         roles: ["admin"] },
      { id: "adminCompanyDetail", label: "Company",       icon: "building",         roles: ["admin"], hidden: true },
      { id: "adminSubscriptions", label: "Subscriptions", icon: "repeat",           roles: ["admin"] },
      { id: "adminBilling",       label: "Billing",       icon: "credit-card",      roles: ["admin"] },
      { id: "adminPlans",         label: "Plans",         icon: "tag",              roles: ["admin"] },
      { id: "adminCoupons",       label: "Coupons",       icon: "ticket",           roles: ["admin"] },
      { id: "adminSystem",        label: "System",        icon: "activity",         roles: ["admin"] },
      { id: "adminIpBlocks",      label: "IP Blocker",    icon: "shield",           roles: ["admin"] },
      { id: "adminAuditLog",      label: "Audit Log",     icon: "file-text",        roles: ["admin"] },
      { id: "adminAdmins",        label: "Admins",        icon: "shield",           roles: ["admin"] },
      { id: "adminSupport",       label: "Support",       icon: "headset",          roles: ["admin"], badgeKey: "support" },
      { id: "adminChangelog",     label: "Latest Updates", icon: "sparkles",        roles: ["admin"] },
      { id: "settings",           label: "Settings",      icon: "settings",         roles: ["admin"] },
    ],
  },
];

function dedupeSidebarSections(groups) {
  const seen = new Map();
  const ordered = [];
  for (const g of groups) {
    if (seen.has(g.section)) {
      const existing = seen.get(g.section);
      const allIds = new Set(existing.items.map((it) => it.id));
      g.items.forEach((it) => { if (!allIds.has(it.id)) existing.items.push(it); });
    } else {
      const copy = { ...g, items: [...g.items] };
      seen.set(g.section, copy);
      ordered.push(copy);
    }
  }
  return ordered;
}

const CRUMBS = {
  dashboard: ["Workspace", "Dashboard"],
  reports: ["Workspace", "Daily Reports"],
  managers: ["Workspace", "Managers"],
  employees: ["Workspace", "Employees"],
  departments: ["Workspace", "Departments"],
  holidays: ["Workspace", "Holidays & Leaves"],
  myCalendar: ["Workspace", "My Calendar"],
  analytics: ["Workspace", "Analytics"],
  projects: ["Workspace", "Projects"],
  projectDetail: ["Workspace", "Project"],
  performance: ["Workspace", "Performance"],
  announcements: ["Workspace", "Announcements"],
  whatsapp: ["Automation", "WhatsApp Bot"],
  reminders: ["Automation", "Reminders"],
  billing: ["Account", "Billing & Plan"],
  activityLog: ["Account", "Activity Log"],
  latestUpdates: ["Account", "Latest Updates"],
  settings: ["Account", "Settings"],
  assignTasks: ["Workspace", "Assign Tasks"],
  myTasks: ["Workspace", "My Tasks"],
  adminOverview:      ["Platform", "Overview"],
  adminCompanies:     ["Platform", "Companies"],
  adminCompanyDetail: ["Platform", "Companies", "Detail"],
  adminSubscriptions: ["Platform", "Subscriptions"],
  adminBilling:       ["Platform", "Billing"],
  adminPlans:         ["Platform", "Plans"],
  adminSystem:        ["Platform", "System"],
  adminAuditLog:      ["Platform", "Audit Log"],
  adminAdmins:        ["Platform", "Admins"],
  adminSupport:       ["Platform", "Support"],
  adminChangelog:     ["Platform", "Latest Updates"],
  adminCoupons:       ["Platform", "Coupons"],
  adminIpBlocks:      ["Platform", "IP Blocker"],
};

function filterPagesForRole(role) {
  const filtered = PAGES.map((grp) => ({
    ...grp,
    items: grp.items.filter((it) => it.roles.includes(role) && !it.hidden),
  })).filter((grp) => grp.items.length > 0);
  return dedupeSidebarSections(filtered);
}

export function isPageAllowed(pageId, role) {
  for (const grp of PAGES) {
    for (const it of grp.items) {
      if (it.id === pageId && it.roles.includes(role)) return true;
    }
  }
  return false;
}

export function Sidebar() {
  const { state, page, goPage, sidebarOpen, setSidebarOpen } = useApp();
  const role = useRole();
  const { data: session } = useSession();
  const sessionUser = session?.user;
  const displayName = sessionUser?.name || state.user.name;
  const displayInitials = (sessionUser?.name || state.user.name || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const displayRole = role ? role.charAt(0).toUpperCase() + role.slice(1) : '';
  const groups = filterPagesForRole(role);
  const [badges, setBadges] = useState({});
  const [company, setCompany] = useState(null);

  useEffect(() => {
    if (role !== "admin") return;
    let cancelled = false;
    const fetchBadges = () => {
      getSupportTicketCount()
        .then((d) => { if (!cancelled) setBadges((b) => ({ ...b, support: d?.count || 0 })); })
        .catch(() => {});
    };
    fetchBadges();
    const id = setInterval(fetchBadges, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [role]);

  // Tenant users (owner/manager/employee) get a Performance badge showing their
  // count of new (unacknowledged) published reviews.
  useEffect(() => {
    if (!role || role === "admin") return;
    let cancelled = false;
    const fetchReviewBadge = () => {
      getReviewsUnreadCount()
        .then((d) => { if (!cancelled) setBadges((b) => ({ ...b, reviews: d?.count || 0 })); })
        .catch(() => {});
    };
    fetchReviewBadge();
    const id = setInterval(fetchReviewBadge, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [role]);

  // Tenant users get a workspace card at the top of the sidebar showing the
  // company name. Skip for platform admins (their "company" is __platform__).
  useEffect(() => {
    if (!role || role === "admin") { setCompany(null); return; }
    let cancelled = false;
    getMe()
      .then((d) => { if (!cancelled) setCompany(d?.company || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [role]);

  // If current page is not allowed for this role, bounce to the role's default landing.
  // Wait until the session has resolved (role !== null) — otherwise we'd bounce the
  // user off their restored page on every refresh while the session is still loading.
  const defaultPage = role === "admin" ? "adminOverview" : "dashboard";
  useEffect(() => {
    if (!role) return;
    if (!isPageAllowed(page, role)) goPage(defaultPage);
  }, [page, role, goPage, defaultPage]);

  return (
    <>
      <div
        className={`nav-back ${sidebarOpen ? "open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />
      <aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="sidebar-logo">
          <span className="nav-logo-icon">
            <Icon name="bar-chart-3" size={16} />
          </span>{" "}
          REPORTFLOW
        </div>
        {company && <WorkspaceBadge company={company} role={role} />}
        <div className="sidebar-nav">
          {groups.map((grp) => (
            <div key={grp.section}>
              <div className="side-section">{grp.section}</div>
              {grp.items.map((it) => {
                const badge = it.badgeKey ? badges[it.badgeKey] : null;
                return (
                  <div
                    key={it.id}
                    className={`side-link ${page === it.id ? "active" : ""}`}
                    onClick={() => goPage(it.id)}
                  >
                    <Icon name={it.icon} size={18} /> {it.label}
                    {it.live && <span className="badge badge-success">Live</span>}
                    {badge > 0 && (
                      <span className="badge badge-error" style={{ marginLeft: "auto" }}>{badge}</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="sidebar-foot">
          {role === "admin" && (
            <div style={{
              background: "var(--error)",
              color: "white",
              padding: "4px 10px",
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1,
              marginBottom: 12,
              textAlign: "center",
            }}>
              PLATFORM
            </div>
          )}
          <AccountSwitcher
            displayName={displayName}
            displayInitials={displayInitials}
            displayRole={displayRole}
            currentUserId={sessionUser?.id}
          />
        </div>
      </aside>
    </>
  );
}

function initialsOf(name) {
  return (name || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

// Account switcher: the sidebar user card opens a menu listing every account
// signed in on this browser (via better-auth multi-session). Click another
// account to switch, "Add account" to sign in to one more, or remove one.
function AccountSwitcher({ displayName, displayInitials, displayRole, currentUserId }) {
  const { goPage, toast } = useApp();
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      // The better-auth client wraps responses as { data, error }; be tolerant
      // of either that or a bare array so the list never silently breaks.
      const res = await authClient.multiSession.listDeviceSessions();
      const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setSessions(list);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) loadSessions(); }, [open, loadSessions]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const switchTo = async (token) => {
    if (busy || !token) return;
    setBusy(true);
    try {
      await authClient.multiSession.setActive({ sessionToken: token });
      window.location.assign("/app"); // full refresh so role/data/sidebar update
    } catch {
      toast?.("Couldn't switch account. Please try again.", "error");
      setBusy(false);
    }
  };

  const revoke = async (token, e) => {
    e.stopPropagation();
    if (busy || !token) return;
    setBusy(true);
    try {
      await authClient.multiSession.revoke({ sessionToken: token });
      await loadSessions();
    } catch {
      toast?.("Couldn't remove that account.", "error");
    } finally {
      setBusy(false);
    }
  };

  const menuItem = {
    width: "100%", display: "flex", alignItems: "center", gap: 10,
    padding: "9px 12px", border: "none", background: "transparent",
    color: "var(--text)", cursor: "pointer", fontSize: 13, textAlign: "left",
  };
  const hoverOn = (e) => { e.currentTarget.style.background = "var(--surface-2)"; };
  const hoverOff = (e) => { e.currentTarget.style.background = "transparent"; };

  // Other accounts on this browser (everything that isn't the active one).
  // The plugin dedupes by user within a browser, so each account appears once.
  const others = sessions.filter((s) => s.user?.id && s.user.id !== currentUserId);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {open && (
        <div
          style={{
            position: "absolute", bottom: "calc(100% + 8px)", left: 0, right: 0,
            background: "var(--surface)", border: "1px solid var(--border)",
            borderRadius: 10, boxShadow: "0 12px 32px rgba(0,0,0,.28)",
            overflow: "hidden", zIndex: 70,
          }}
        >
          <div style={{ padding: "10px 12px 6px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--text-dim)" }}>
            Accounts
          </div>
          {/* Current (active) account — always shown from the live session, so
              this is robust even before listDeviceSessions resolves. */}
          <div style={{ ...menuItem, cursor: "default" }}>
            <div className="avatar sm">{displayInitials}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{displayName}</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{displayRole}</div>
            </div>
            <span style={{ fontSize: 10, fontWeight: 700, color: "var(--success)", textTransform: "uppercase", flexShrink: 0 }}>Active</span>
          </div>
          {loading && others.length === 0 && (
            <div style={{ padding: "4px 12px 10px", fontSize: 12.5, color: "var(--text-muted)" }}>Loading…</div>
          )}
          {others.map((s) => (
            <div
              key={s.session?.token}
              onClick={() => switchTo(s.session?.token)}
              style={{ ...menuItem, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1 }}
              onMouseEnter={hoverOn}
              onMouseLeave={hoverOff}
            >
              <div className="avatar sm">{initialsOf(s.user?.name)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.user?.name || "—"}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.user?.email}</div>
              </div>
              <span
                onClick={(e) => revoke(s.session?.token, e)}
                title="Remove this account"
                style={{ display: "grid", placeItems: "center", width: 22, height: 22, borderRadius: 6, color: "var(--text-muted)", flexShrink: 0 }}
              >
                <Icon name="x" size={13} />
              </span>
            </div>
          ))}
          <div style={{ height: 1, background: "var(--border)", margin: "6px 0" }} />
          <button style={menuItem} disabled={busy} onMouseEnter={hoverOn} onMouseLeave={hoverOff}
            onClick={() => { window.location.assign("/signin?addAccount=1"); }}>
            <Icon name="plus" size={15} /> Add account
          </button>
          <button style={menuItem} onMouseEnter={hoverOn} onMouseLeave={hoverOff}
            onClick={() => { setOpen(false); goPage("settings"); }}>
            <Icon name="settings" size={15} /> Account settings
          </button>
        </div>
      )}
      <div className="user-card" onClick={() => setOpen((o) => !o)}>
        <div className="avatar">{displayInitials}</div>
        <div className="info">
          <div className="name">{displayName}</div>
          <div className="role">{displayRole}</div>
        </div>
        <Icon name={open ? "chevron-down" : "chevron-up"} size={14} />
      </div>
    </div>
  );
}

function WorkspaceBadge({ company, role }) {
  // Employees only see the company name (no plan/status line), so the badge is
  // made more compact and the name smaller to stay balanced.
  const isEmployee = role === "employee";
  const initials = (company?.name || "?")
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const planLabel = (() => {
    switch (company?.subscriptionPlan) {
      case "free_trial": return "Free Trial";
      case "starter":    return "Starter Plan";
      case "growth":     return "Growth Plan";
      default:           return "Workspace";
    }
  })();
  const trialDaysLeft = (() => {
    if (company?.subscriptionPlan !== "free_trial" || !company?.trialEnd) return null;
    const ms = new Date(company.trialEnd).getTime() - Date.now();
    if (!Number.isFinite(ms)) return null;
    return Math.max(0, Math.ceil(ms / 86400000));
  })();
  return (
    <div
      style={{
        flexShrink: 0,
        margin: "2px 12px 10px",
        padding: isEmployee ? "8px 11px" : "9px 11px",
        borderRadius: 11,
        background:
          "linear-gradient(135deg, rgba(139,92,246,0.16), rgba(99,102,241,0.09) 60%, rgba(59,130,246,0.05))",
        border: "1px solid rgba(139,92,246,0.28)",
        boxShadow: "0 2px 10px rgba(139,92,246,0.07)",
        display: "flex",
        alignItems: "center",
        gap: isEmployee ? 9 : 10,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 90% 0%, rgba(167,139,250,0.22), transparent 72%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          flexShrink: 0,
          width: isEmployee ? 32 : 34,
          height: isEmployee ? 32 : 34,
          borderRadius: 9,
          background: "linear-gradient(135deg, #8B5CF6, #6366F1)",
          color: "#fff",
          display: "grid",
          placeItems: "center",
          fontSize: isEmployee ? 12 : 13,
          fontWeight: 800,
          letterSpacing: 0.3,
          boxShadow: "0 4px 12px rgba(139,92,246,0.35)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {initials}
      </div>
      <div style={{ flex: 1, minWidth: 0, position: "relative", zIndex: 1 }}>
        {/* No "Workspace" eyebrow here — it duplicated the nav section header
            directly below the badge. Company name is the first line. */}
        <div
          title={company?.name}
          style={{
            fontSize: isEmployee ? 12.5 : 13.5,
            fontWeight: isEmployee ? 600 : 700,
            background: "linear-gradient(135deg, #c4b5fd, #a78bfa, #818cf8)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: 1.15,
          }}
        >
          {company?.name || "—"}
        </div>
        {/* Plan/status line — hidden for employees (company name only). */}
        {role !== "employee" && (
        <div
          style={{
            fontSize: 11,
            color: "var(--text-muted)",
            marginTop: 1,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: company?.isActive ? "#22c55e" : "#ef4444",
              boxShadow: company?.isActive
                ? "0 0 0 2px rgba(34,197,94,0.18)"
                : "0 0 0 2px rgba(239,68,68,0.18)",
            }}
          />
          {planLabel}
          {trialDaysLeft != null && (
            <span style={{ color: "var(--text-muted)" }}>
              {" · "}
              {trialDaysLeft === 0 ? "ends today" : `${trialDaysLeft}d left`}
            </span>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

function PreferencesDropdown() {
  const { state, setSetting, toggleTheme } = useApp();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const theme = state.settings.theme || "dark";
  const fontSize = state.settings.fontSize || "default";
  const density = state.settings.density || "default";

  return (
    <div className="pref-wrap" ref={ref}>
      <button className="icon-btn" onClick={() => setOpen(!open)} title="Preferences">
        <Icon name="sliders-horizontal" size={16} />
      </button>
      {open && (
        <div className="pref-panel">
          <div className="pref-section">
            <div className="pref-label">Theme</div>
            <div className="seg-group">
              <button className={`seg-btn ${theme === "light" ? "active" : ""}`} onClick={() => { if (theme !== "light") toggleTheme(); }}>
                <Icon name="sun" size={14} /> Light
              </button>
              <button className={`seg-btn ${theme === "dark" ? "active" : ""}`} onClick={() => { if (theme !== "dark") toggleTheme(); }}>
                <Icon name="moon" size={14} /> Dark
              </button>
            </div>
          </div>
          <div className="pref-section">
            <div className="pref-label">Text Size</div>
            <div className="seg-group">
              {[["small", "S"], ["default", "M"], ["large", "L"]].map(([val, label]) => (
                <button key={val} className={`seg-btn ${fontSize === val ? "active" : ""}`} onClick={() => setSetting("fontSize", val)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="pref-section">
            <div className="pref-label">Density</div>
            <div className="seg-group">
              {[["compact", "Compact"], ["default", "Default"], ["comfortable", "Roomy"]].map(([val, label]) => (
                <button key={val} className={`seg-btn ${density === val ? "active" : ""}`} onClick={() => setSetting("density", val)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Extra search keywords per page so the top search matches synonyms, not just
// the visible label (e.g. "leave" → Holidays & Leaves, "staff" → Employees).
const PAGE_ALIASES = {
  dashboard: ["home", "overview"],
  reports: ["report", "daily report", "standup", "submission"],
  projects: ["project"],
  projectDetail: ["project"],
  managers: ["manager"],
  employees: ["employee", "staff", "team", "people", "member"],
  assignTasks: ["assign", "task", "todo"],
  myTasks: ["task", "todo", "my work"],
  departments: ["department", "dept", "team"],
  holidays: ["holiday", "leave", "vacation", "time off"],
  myCalendar: ["calendar", "schedule"],
  analytics: ["analytic", "stats", "metrics", "insight"],
  performance: ["performance", "review", "appraisal", "rating"],
  announcements: ["announcement", "notice", "broadcast"],
  whatsapp: ["whatsapp", "wa", "bot", "chatbot"],
  reminders: ["reminder", "nudge", "notification"],
  billing: ["billing", "plan", "subscription", "upgrade", "invoice", "payment"],
  activityLog: ["activity", "log", "audit"],
  settings: ["setting", "profile", "account", "preference", "password"],
};

// Topbar page search: type a keyword → matching pages drop down → click (or
// Enter) navigates. Searches only pages the current role can actually see.
function TopSearch() {
  const { goPage } = useApp();
  const role = useRole();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef(null);

  const index = useMemo(() => {
    if (!role) return [];
    return filterPagesForRole(role).flatMap((g) =>
      g.items.map((it) => ({ id: it.id, label: it.label, section: g.section, icon: it.icon })),
    );
  }, [role]);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return index
      .filter(
        (it) =>
          it.label.toLowerCase().includes(term) ||
          it.section.toLowerCase().includes(term) ||
          (PAGE_ALIASES[it.id] || []).some((a) => a.includes(term)),
      )
      .slice(0, 8);
  }, [q, index]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const choose = (it) => {
    if (!it) return;
    goPage(it.id);
    setQ("");
    setOpen(false);
  };

  const onKey = (e) => {
    if (e.key === "Escape") { setOpen(false); return; }
    if (!results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % results.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + results.length) % results.length); }
    else if (e.key === "Enter") { e.preventDefault(); choose(results[active] || results[0]); }
  };

  return (
    <div className="top-search" ref={ref}>
      <Icon name="search" size={15} />
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder="Search pages…"
        aria-label="Search pages"
      />
      {open && results.length > 0 && (
        <div className="top-search-results">
          {results.map((it, i) => (
            <button
              key={it.id}
              type="button"
              className={`top-search-item ${i === active ? "active" : ""}`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => { e.preventDefault(); choose(it); }}
            >
              <Icon name={it.icon} size={15} />
              <span className="ts-label">{it.label}</span>
              <span className="ts-section">{it.section}</span>
            </button>
          ))}
        </div>
      )}
      {open && q.trim() && results.length === 0 && (
        <div className="top-search-results">
          <div className="top-search-empty">No pages match "{q.trim()}"</div>
        </div>
      )}
    </div>
  );
}

export function Topbar() {
  const { page, goPage, setSidebarOpen, openModal, closeModal, notifications, setNotifOpen } = useApp();
  const role = useRole();
  const crumb = CRUMBS[page] || ["Workspace", ""];
  const unreadNotifs = (notifications || []).filter((n) => !n.readAt).length;

  const handleSignOut = async () => {
    openModal(
      <div style={{ position: "relative" }}>
        <button
          className="icon-btn"
          onClick={closeModal}
          style={{ position: "absolute", top: 14, right: 14, zIndex: 2 }}
          aria-label="Close"
        >
          <Icon name="x" size={16} />
        </button>
        <div style={{ padding: "28px 26px 18px", textAlign: "center" }}>
          <div
            className="signout-icon"
            style={{
              width: 54,
              height: 54,
              borderRadius: "50%",
              margin: "0 auto 16px",
              display: "grid",
              placeItems: "center",
              color: "var(--primary-2)",
              background: "rgba(139,92,246,0.12)",
              border: "1px solid rgba(139,92,246,0.25)",
            }}
          >
            <Icon name="log-out" size={24} />
          </div>
          <h3 className="signout-rise" style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
            Sign out?
          </h3>
          <p
            className="signout-rise"
            style={{
              fontSize: 13,
              color: "var(--text-muted)",
              lineHeight: 1.5,
              margin: "7px auto 0",
              maxWidth: 280,
              animationDelay: "70ms",
            }}
          >
            You'll be signed out of your account and need to sign in again to return.
          </p>
        </div>
        <div
          className="modal-foot"
          style={{ borderTop: "none", paddingTop: 0, justifyContent: "center" }}
        >
          <button className="btn btn-ghost" onClick={closeModal} style={{ minWidth: 116 }}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            style={{ minWidth: 116 }}
            onClick={async () => {
              closeModal();
              try {
                await signOut();
              } catch {}
              // Clear identity-bearing cached state so the next person on this
              // browser never sees the previous user's name/workspace flash in.
              // (The session itself is cookie-based; this only prevents stale
              // localStorage identity bleed.) UI prefs like theme are kept.
              try {
                localStorage.removeItem("rfState_react");
                localStorage.removeItem("botWidget.state");
                localStorage.removeItem("currentPage");
              } catch {}
              // Hard redirect so the in-memory store is fully reset too.
              window.location.assign("/");
            }}
          >
            <Icon name="log-out" size={14} /> Sign out
          </button>
        </div>
      </div>,
      "sm",
    );
  };

  return (
    <header className="topbar">
      <div style={{ display: "flex", alignItems: "center", gap: 14, flex: 1, minWidth: 0 }}>
        <button
          className="icon-btn nav-toggle"
          onClick={() => setSidebarOpen(true)}
        >
          <Icon name="menu" size={16} />
        </button>
        <div className="crumbs">
          {crumb.map((c, i) =>
            i === crumb.length - 1 ? (
              <span key={i} className="current">
                {c}
              </span>
            ) : (
              <Fragment key={i}>
                <span>{c}</span>
                <span className="sep">/</span>
              </Fragment>
            ),
          )}
        </div>
        <TopSearch />
      </div>
      <div className="top-actions" style={{ marginLeft: "auto" }}>
        {role !== "admin" && (
          <button
            className="icon-btn"
            onClick={() => setNotifOpen(true)}
            title="Notifications"
            style={{ position: "relative" }}
          >
            <Icon name="bell" size={16} />
            {unreadNotifs > 0 && (
              <span
                className="badge badge-error"
                style={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  minWidth: 16,
                  height: 16,
                  padding: "0 4px",
                  fontSize: 10,
                  lineHeight: "16px",
                  textAlign: "center",
                  borderRadius: 8,
                }}
              >
                {unreadNotifs > 9 ? "9+" : unreadNotifs}
              </span>
            )}
          </button>
        )}
        <PreferencesDropdown />
        <button
          className={`icon-btn ${page === "settings" ? "active" : ""}`}
          onClick={() => goPage("settings")}
          title="Profile & Settings"
          aria-label="Profile and settings"
        >
          <Icon name="user" size={16} />
        </button>
        <button className="icon-btn" onClick={handleSignOut} title="Sign out" aria-label="Sign out">
          <Icon name="log-out" size={16} />
        </button>
      </div>
    </header>
  );
}
